import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '@services/auth.service';

export declare type UserRole = 'OWNER/ADMIN' | 'OPTICIAN/INVENTORY';

export interface NavConfig {
  path: string;
  label: string;
}

export interface NavGroup {
  title: string;
  links: NavConfig[];
}

export interface RoleNavConfig {
  [key: string]: Array<NavConfig>;
}

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css'],
})
export class AdminAdminComponent implements OnDestroy {
  @ViewChild('menuButton') menuButton: ElementRef<HTMLButtonElement>;

  userRole: string;
  userNav: Array<NavConfig>;
  navGroups: NavGroup[] = [];
  menuOpen = true;
  currentLabel = '';

  defaultNav: Array<NavConfig> = [
    { path: 'reports', label: 'REPORTS' },
    { path: 'fix-orders', label: 'FIX ORDERS' },
    { path: 'fix-duplicate-products', label: 'FIX DUPLICATES' },
    { path: 'fix-duplicate-products-size', label: 'FIX DUPLICATES (NO SIZE)' },
  ];

  roleNavConfig: RoleNavConfig = {
    ['OWNER/ADMIN']: [
      { path: 'patients', label: 'QUERY CUSTOMERS' },
      { path: 'import-customers', label: 'IMPORT CUSTOMERS' },
      { path: 'users', label: 'EMPLOYEES' },
      { path: 'favorites', label: 'FAVORITES' },
      { path: 'duty-settings', label: 'PRICING' },
      { path: 'discounts', label: 'DISCOUNTS' },
      { path: 'trunk-shows', label: 'TRUNK SHOWS' },
      { path: 'acknowledgment', label: 'ACKNOWLEDGMENT' },
      { path: 'payable', label: 'PAYABLE' },
      { path: 'receivable', label: 'RECEIVABLE' },
      ...this.defaultNav,
      { path: 'shopify-data', label: 'SHOPIFY' },
    ],
  };

  private routerSub: Subscription;

  constructor(public auth: AuthService, private router: Router) {
    this.userRole = this.auth.currentUser?.role;
    this.userNav = this.roleNavConfig[this.userRole] ?? this.defaultNav;
    this.navGroups = this.buildGroups();
    this.syncCurrent();
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncCurrent());
  }

  get headerColor(): string {
    return this.auth.getStoreObject?.()?.headerColor || '#4eabac';
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    if (!this.menuOpen) {
      return;
    }
    this.menuOpen = false;
    this.menuButton?.nativeElement?.focus();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMenu();
  }

  isActive(path: string): boolean {
    return this.currentPath() === path;
  }

  private buildGroups(): NavGroup[] {
    const allowed = new Set(this.userNav.map((link) => link.path));
    const pick = (links: NavConfig[]) =>
      links.filter((link) => allowed.has(link.path));
    return [
      {
        title: 'CUSTOMERS',
        links: pick([
          { path: 'patients', label: 'QUERY CUSTOMERS' },
          { path: 'import-customers', label: 'IMPORT CUSTOMERS' },
        ]),
      },
      {
        title: 'PEOPLE',
        links: pick([{ path: 'users', label: 'EMPLOYEES' }]),
      },
      {
        title: 'FINANCIAL',
        links: pick([
          { path: 'payable', label: 'PAYABLE' },
          { path: 'receivable', label: 'RECEIVABLE' },
          { path: 'reports', label: 'REPORTS' },
        ]),
      },
      {
        title: 'SETTINGS',
        links: pick([
          { path: 'favorites', label: 'FAVORITES' },
          { path: 'duty-settings', label: 'PRICING' },
          { path: 'discounts', label: 'DISCOUNTS' },
          { path: 'trunk-shows', label: 'TRUNK SHOWS' },
          { path: 'acknowledgment', label: 'ACKNOWLEDGMENT' },
        ]),
      },
      {
        title: 'TOOLS',
        links: pick([
          { path: 'fix-orders', label: 'FIX ORDERS' },
          { path: 'fix-duplicate-products', label: 'FIX DUPLICATES' },
          { path: 'fix-duplicate-products-size', label: 'FIX DUPLICATES (NO SIZE)' },
          { path: 'shopify-data', label: 'SHOPIFY' },
        ]),
      },
    ].filter((group) => group.links.length);
  }

  private syncCurrent(): void {
    const path = this.currentPath();
    const link = this.userNav.find(
      (item) => item.path === path || (path && path.startsWith(item.path))
    );
    this.currentLabel = link?.label || '';
  }

  private currentPath(): string {
    const match = this.router.url.match(/\(sub:([^/?)]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }
}
